import type { StyleLibrary } from "schemas/generated/project";

import { useMemo } from "react";

interface ProcessedColor {
  name: string;
  key: string;
  value: string;
}

interface UseDesignLibraryColorsOptions {
  includeEmpty?: boolean;
  defaultColor?: string;
}

interface UseDesignLibraryColorsReturn {
  neutralColors: ProcessedColor[];
  brandColors: ProcessedColor[];
  allColors: ProcessedColor[];
  hasAllBrandColors: boolean;
}

const BRAND_COLOR_KEYS = [
  "primary",
  "secondary",
  "tertiary",
  "accent",
] as const;

export const useDesignLibraryColors = (
  styleLibrary: StyleLibrary | null,
  options: UseDesignLibraryColorsOptions = {},
): UseDesignLibraryColorsReturn => {
  const { includeEmpty = false, defaultColor = "#FFFFFF" } = options;

  return useMemo(() => {
    const colorsFromProjectStyleLibrary = styleLibrary?.colors;

    const rawNeutralColors = [
      {
        name: "Light",
        key: "light",
        value: colorsFromProjectStyleLibrary?.neutrals?.light,
      },
      {
        name: "Subtle",
        key: "subtle",
        value: colorsFromProjectStyleLibrary?.neutrals?.subtle,
      },
      {
        name: "Muted",
        key: "muted",
        value: colorsFromProjectStyleLibrary?.neutrals?.muted,
      },
      {
        name: "Dark",
        key: "dark",
        value: colorsFromProjectStyleLibrary?.neutrals?.dark,
      },
    ];

    const neutralColors = rawNeutralColors
      .map((color) => ({
        ...color,
        value: color.value ?? (includeEmpty ? defaultColor : ""),
      }))
      .filter((color): color is ProcessedColor =>
        includeEmpty ? true : Boolean(color.value && color.value.trim() !== ""),
      );

    // Process brand colors
    const rawBrandColors = BRAND_COLOR_KEYS.map((key) => ({
      name:
        colorsFromProjectStyleLibrary?.brand?.[key]?.name ??
        key.charAt(0).toUpperCase() + key.slice(1),
      key,
      value:
        colorsFromProjectStyleLibrary?.brand?.[key]?.color ??
        (includeEmpty ? defaultColor : ""),
    }));

    const brandColors = rawBrandColors.filter((color) =>
      includeEmpty ? true : Boolean(color.value && color.value.trim() !== ""),
    );

    const allColors = [...neutralColors, ...brandColors];

    // Check if all brand colors are present
    const hasAllBrandColors = BRAND_COLOR_KEYS.every(
      (key) =>
        colorsFromProjectStyleLibrary?.brand?.[key]?.color &&
        colorsFromProjectStyleLibrary.brand[key].color.trim() !== "",
    );

    return {
      neutralColors,
      brandColors,
      allColors,
      hasAllBrandColors,
    };
  }, [styleLibrary, includeEmpty, defaultColor]);
};
